import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LoadingState } from '@/components/index.js';
import { useAuth } from '@/lib/auth/AuthProvider.js';

/**
 * Gate for every authenticated route.
 *
 * Three states, and the middle one matters: while the session query is still
 * in flight we must render neither the page nor a redirect. Redirecting on
 * "not loaded yet" bounces a signed-in user to the sign-in screen on every
 * hard refresh, which is the single most common bug in this pattern.
 *
 * The attempted URL is carried in router state so that after signing in the
 * user lands where they were going, not on a generic home page.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, status } = useAuth();
  const location = useLocation();

  if (status === 'pending') {
    return <LoadingState variant="spinner" label="Checking your session" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/** Keeps a signed-in user off the sign-in screen. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated, status } = useAuth();

  if (status === 'pending') {
    return <LoadingState variant="spinner" label="Checking your session" />;
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
}
